import { memo, useMemo } from 'react'
import type { ToolRendererComponent } from '@/config/types'
import { useAgentRuntime } from '@/context/AgentProvider'
import { ToolCallDebug } from './ToolCallDebug'
import { ToolCallHeader } from './ToolCallHeader'
import type { ToolCallCardProps } from './types'
import ToolApproval from './ToolApproval'
import { SubagentToolCall } from './SubagentToolCall'

export const ToolCallCard = memo(({ call, contextKey, messageId, chatId }: ToolCallCardProps) => {
  const runtime = useAgentRuntime()
  const subagentChatId =
    call.subagentChatId || (typeof call.input?.subagentChatId === 'string' ? call.input.subagentChatId : undefined)

  // Renderer resolution only depends on the tool's name — keying the memo on
  // `call` itself would recompute on every streaming update.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const CustomRenderer = useMemo(() => runtime.getToolRenderer(call), [runtime, call.name]) as
    | ToolRendererComponent
    | undefined

  // The subagent thread renders below the tool call's own output, whether that
  // output comes from a custom renderer or the default chrome.
  const subagent = subagentChatId ? <SubagentToolCall call={call} subagentChatId={subagentChatId} /> : null

  // Without a custom renderer the panel only has meaningful content when there's
  // a subagent to preview, debug output, or an approval to show — pending buttons
  // or the answered question/response card (both persist requiresConfirmation +
  // approvalId, so answered approvals keep rendering). Skip otherwise so we
  // don't emit empty tool panels.
  if (!CustomRenderer) {
    const hasApproval = Boolean(call.requiresConfirmation && call.approvalId)
    const hasDefaultContent = Boolean(subagentChatId) || runtime.config.debug || hasApproval
    if (!hasDefaultContent) {
      return null
    }

    return (
      <div className='mp-agent-tool-panel'>
        <ToolCallHeader call={call} />
        <ToolApproval call={call} messageId={messageId} chatId={chatId} />
        <ToolCallDebug call={call} />
        {subagent}
      </div>
    )
  }

  return (
    <div className='mp-agent-tool-panel'>
      <CustomRenderer call={call} contextKey={contextKey} chatId={chatId} />
      <ToolApproval call={call} messageId={messageId} chatId={chatId} />
      <ToolCallDebug call={call} />
      {subagent}
    </div>
  )
})

ToolCallCard.displayName = 'ToolCallCard'
